import {
    createElement,
    getAttributes,
    insertElements,
    prerenderElementsTree,
} from './render-api.js';
import type {
    Attributes,
    ElementConfig,
} from '../components/ui-element.type.js';

type MutationCallback = (element: HTMLElement) => void;
export type SmoothAppendCallback = (
    sourceElement: HTMLElement,
    targetElement?: HTMLElement
) => void;
export type SmoothRemoveCallback = (element: HTMLElement) => void;
type SortHandlerCallback = (element: HTMLElement) => void;

/**
 * Mutates attributes object.
 * @memberof UiHelpers
 * @param {Record<string, string>} oldAttributes
 * @param {Record<string, string>} newAttributes
 * @returns {Function}
 */
const mutateAttributesTo = (
    oldAttributes: Attributes,
    newAttributes: Attributes
): MutationCallback => {
    const patches: Array<MutationCallback> = [];
    const ignoredAttributes = ['state', 'expanded'];
    if (oldAttributes['data-transition']) {
        ignoredAttributes.push('class', 'data-transition');
    }

    for (const oldKey in oldAttributes) {
        if (!oldAttributes.hasOwnProperty(oldKey)) {
            continue;
        }
        if (ignoredAttributes.includes(oldKey)) {
            continue;
        }
        if (!(oldKey in newAttributes)) {
            patches.push(
                (function (attr: string) {
                    return (element: Element): void =>
                        element.removeAttribute(attr);
                })(oldKey)
            );
        }
    }

    for (const key in newAttributes) {
        if (!newAttributes.hasOwnProperty(key)) {
            continue;
        }
        if (ignoredAttributes.includes(key)) {
            continue;
        }
        if (newAttributes[key] !== oldAttributes[key]) {
            patches.push(
                (function (attr) {
                    return (element: Element): void => {
                        switch (typeof newAttributes[attr]) {
                            case 'boolean':
                                newAttributes[attr]
                                    ? element.setAttribute(attr, '')
                                    : element.removeAttribute(attr);
                                break;
                            case 'object':
                                break;
                            default:
                                element.setAttribute(
                                    attr,
                                    String(newAttributes[attr])
                                );
                        }
                    };
                })(key)
            );
        }
    }

    return (element: HTMLElement): void =>
        patches.forEach((patch): void => patch(element));
};

/**
 * It will create a path to mutate old element to new element.
 * @memberof UiHelpers
 * @param {HTMLElement} oldElement
 * @param {HTMLElement} newElement
 * @param {boolean} [immediately] - indicate if patch should be applied immediately
 * @returns {Function}
 */
const mutateTo = (
    oldElement: HTMLElement,
    newElement: HTMLElement,
    immediately: boolean = false
): MutationCallback => {
    const shouldReplaceElement =
        oldElement.tagName && newElement.tagName
            ? oldElement.tagName !== newElement.tagName
            : oldElement.nodeType !== newElement.nodeType ||
              oldElement.nodeValue !== newElement.nodeValue;

    if (shouldReplaceElement) {
        return (element: HTMLElement): HTMLElement | null => {
            const parentElement = element.parentNode;
            if (parentElement) {
                parentElement.insertBefore(newElement, element);
                parentElement.removeChild(element);
                return newElement;
            }
            return null;
        };
    }

    const patches: Array<MutationCallback> = [];

    if (oldElement.tagName && newElement.tagName) {
        patches.push(
            mutateAttributesTo(
                getAttributes(oldElement),
                getAttributes(newElement)
            ),
            mutateChildrenTo(
                [].slice.call(oldElement.childNodes),
                [].slice.call(newElement.childNodes)
            )
        );
    }
    const result = (element: HTMLElement): void =>
        patches.forEach((patch): void => patch(element));

    if (immediately) {
        prerenderElementsTree(newElement, true);
        result(oldElement);
    }

    return result;
};

/**
 * Mutates children object.
 * @memberof UiHelpers
 * @param {Array<HTMLElement> | NodeListOf<HTMLElement>} oldChildren
 * @param {Array<HTMLElement> | NodeListOf<HTMLElement>} newChildren
 * @param {Function} [smoothRemove]
 * @param {Function} [smoothAppend]
 * @returns {Function}
 */
const mutateChildrenTo = (
    oldChildren: Array<HTMLElement> | NodeListOf<HTMLElement>,
    newChildren: Array<HTMLElement> | NodeListOf<HTMLElement>,
    smoothRemove?: SmoothRemoveCallback,
    smoothAppend?: SmoothAppendCallback
): MutationCallback => {
    if (!Array.isArray(oldChildren)) {
        oldChildren = [].slice.call(oldChildren);
    }
    if (!Array.isArray(newChildren)) {
        newChildren = [].slice.call(newChildren);
    }

    const patches: Array<MutationCallback> = [];

    const createAppendHandler = (
        parentElement: HTMLElement
    ): MutationCallback => {
        return (element: HTMLElement): void => {
            if (smoothAppend) {
                return smoothAppend(element, parentElement);
            }
            parentElement?.appendChild(element);
        };
    };

    const createRemoveHandler = (
        parentElement: HTMLElement
    ): SmoothRemoveCallback => {
        return (element: HTMLElement): void => {
            if (!element || parentElement !== element.parentElement) {
                return;
            }
            if (smoothRemove) {
                return smoothRemove(element);
            }
            parentElement?.removeChild(element);
        };
    };

    const createMutateHandler = (
        oldElement: HTMLElement,
        newElement: HTMLElement
    ): MutationCallback => {
        return (): void => {
            mutateTo(oldElement, newElement)(oldElement);
        };
    };

    const createSortHandler = (
        oldElement: HTMLElement,
        index: number
    ): SortHandlerCallback => {
        return (parentNode: HTMLElement): void => {
            if (!parentNode) {
                return;
            }
            parentNode.insertBefore(oldElement, parentNode.childNodes[index]);
        };
    };

    const oldMap: Record<string, HTMLElement> = {};

    oldChildren.forEach((element: HTMLElement): void => {
        const key =
            element.nodeType === Node.ELEMENT_NODE
                ? element.getAttribute('key')
                : null;
        if (key) {
            oldMap[key] = element;
        }
    });

    const sortPatches: Array<MutationCallback> = [];

    newChildren.forEach((element, index): void => {
        const key =
            element.nodeType === Node.ELEMENT_NODE
                ? element.getAttribute('key')
                : null;
        if (key) {
            if (oldMap[key]) {
                const oldIndex = [...oldChildren].indexOf(oldMap[key]);
                if (oldIndex === index) {
                    return;
                }
                // Unclear logic below, seems that this is an abuse of JS runtime.
                if (oldChildren.length - 1 < index) {
                    Reflect.defineProperty(oldChildren, 'length', {
                        value: index + 1,
                    });
                }
                oldChildren = [].map.call(
                    oldChildren,
                    (item: HTMLElement, index) => {
                        return oldIndex === index ? undefined : item;
                    }
                ) as Array<HTMLElement>;
                oldChildren.splice(index, 0, oldMap[key]);
                sortPatches.push(createSortHandler(oldMap[key], index));
            }
        }
    });

    if (!oldChildren.length) {
        patches.push((element: HTMLElement): void => {
            [].forEach.call(newChildren, createAppendHandler(element));
        });
    } else if (newChildren.length < oldChildren.length) {
        const toRemove = [].slice.call(oldChildren, newChildren.length);
        patches.push((element): void => {
            toRemove.forEach(createRemoveHandler(element));
        });
    } else if (newChildren.length > oldChildren.length) {
        const toAdd = newChildren.slice(oldChildren.length);
        patches.push((element): void => {
            toAdd.forEach(createAppendHandler(element));
        });
    }

    for (
        let index = 0;
        index < Math.min(newChildren.length, oldChildren.length);
        index++
    ) {
        patches.push(
            (function (index) {
                if (!oldChildren[index]) {
                    sortPatches.push(
                        createSortHandler(newChildren[index], index)
                    );
                    return (parentElement: HTMLElement): void => {
                        createAppendHandler(parentElement)(newChildren[index]);
                    };
                }
                return createMutateHandler(
                    oldChildren[index],
                    newChildren[index]
                );
            })(index)
        );
    }

    return (element: HTMLElement): void => {
        patches.forEach((patch): void => patch(element));
        sortPatches.forEach((patch): void => patch(element));
    };
};

/**
 * @memberof UiHelpers
 * @param {HTMLElement} targetElement
 * @param {Array<ElementConfig | Node> | DocumentFragment} source
 * @param {Function} [smoothRemove]
 * @param {Function} [smoothAppend]
 * @returns {HTMLElement}
 */
const rebuildChildren = (
    targetElement: HTMLElement,
    source: Array<ElementConfig | Node> | DocumentFragment,
    smoothRemove?: SmoothAppendCallback,
    smoothAppend?: SmoothAppendCallback
): HTMLElement => {
    const elements =
        source instanceof DocumentFragment ? [...source.childNodes] : source;
    const newElements = elements.reduce<Array<HTMLElement>>(
        (collection: Array<HTMLElement>, entry: Node | ElementConfig) => {
            return collection.concat(
                [entry].map((item: Node | ElementConfig) => {
                    if (entry instanceof Node) {
                        return entry as HTMLElement;
                    }
                    const element = createElement(
                        item as ElementConfig
                    ) as HTMLElement;
                    prerenderElementsTree(element, true);
                    return element;
                })
            );
        },
        []
    );

    const oldElements: Array<HTMLElement> = [].filter.call(
        targetElement.childNodes,
        (node: ChildNode | HTMLElement): boolean => {
            return (
                node.nodeType === Node.ELEMENT_NODE &&
                'classList' in node &&
                !node.classList.contains('-removing')
            );
        }
    );
    if (!oldElements.length) {
        insertElements(targetElement, newElements);
    } else {
        mutateChildrenTo(
            oldElements,
            newElements,
            smoothRemove,
            smoothAppend
        )(targetElement);
    }

    return targetElement;
};

/**
 * @memberof UiHelpers
 * @param {HTMLElement} element
 * @param {ElementConfig | Node} newElement
 * @returns {HTMLElement}
 */
const rebuild = (
    element: HTMLElement,
    newElement: ElementConfig | Node
): HTMLElement | Element => {
    const targetElement =
        newElement instanceof Node ? newElement : createElement(newElement);
    prerenderElementsTree(targetElement as HTMLElement, true);
    mutateTo(element, targetElement as HTMLElement, true);
    return element;
};

export {
    mutateTo,
    mutateChildrenTo,
    mutateAttributesTo,
    rebuildChildren,
    rebuild,
};
